/**
 * Get the correct indicator color based on the environment.
 * @returns {string} The color code.
 */
export const getEnvColor = (): string => {
	const env = import.meta.env.VITE_ENVIRONMENT

	const envMap = {
		local: '#80A0FF', // Blue
		develop: '#70D070', // Green
		staging: '#FFA31A', // Orange
		production: '#C04B7B', // Pink
	}

	// If env not found, return an obvious bad color
	return envMap[env] || '#000000'
}

/**
 * Generate a repeatable color from a string.
 * Adopted from https://stackoverflow.com/a/16348977/208079
 *
 * @param str The string to generate a color from.
 * @returns A hex color string from the predefined set.
 */
export function stringToColor(str: string): string {
	const predefinedColors = [
		'#C04B7B',
		'#303E6F',
		'#49A4DA',
		'#7F188D',
		'#E36060',
		'#E06017',
		'#18BD82',
		'#DF99F0',
		'#197278',
		'#16BAC5',
		'#89A1EF',
		'#1446A0',
	]
	let hash = 0
	for (let i = 0; i < str.length; i++) {
		// eslint-disable-next-line no-bitwise
		hash = str.charCodeAt(i) + ((hash << 5) - hash)
	}

	const index = Math.abs(hash) % predefinedColors.length
	return predefinedColors[index]
}

/*
 * Get a test id from a component.
 * A fallback value can be provided if no testid is present in the props
 *
 * @param {object} props - The props object for the component
 * @param {string} fallback - A value that will be returned if no testid is present in props
 * @return {string} The testid for the component
 */
export const getTestIdError = 'getTestId requires an object for the props argument'
export const getTestId = (props: Record<string, any>, fallback?: string): string => {
	if (!props || typeof props !== 'object') throw new Error(getTestIdError)

	if ('data-testid' in props) {
		const value = props['data-testid']
		// If we find the testid, it needs to be removed
		// If not and {...rest} is added to the component, the rest value will overwrite the testid

		try {
			// eslint-disable-next-line no-param-reassign
			delete props['data-testid']
		} catch (error) {
			// console.warn('Failed to delete data-testid property:', error)
			return fallback
		}

		return value
	}

	return fallback || ''
}

/**
 * Remove all objects in an array that exist in another array. This assumes the arrays
 * contain objects with a key that can be compared.
 * @param {Array} arr1 - The array to remove objects from
 * @param {Array} arr2 - The array of objects to remove
 * @param {string} key - The key to compare the objects on
 */
export const removeObjects = (arr1: any[], arr2: any[], key: string): any[] => {
	if (!Array.isArray(arr2)) return arr1
	const filtered = arr1.filter((item) => !arr2.some((other) => item[key] === other[key]))
	return filtered
}

export const getAbbreviation = (text: string) => {
	if (typeof text !== 'string' || !text) {
		return ''
	}
	const res = text ? text.match(/[\p{Alpha}\p{Nd}]+/gu) : null
	const acronym = res
		? res
				.reduce(
					(previous, next) =>
						previous + (+next === 0 || parseInt(next, 10) ? parseInt(next, 10) : next[0] || ''),
					''
				)
				.toUpperCase()
		: ''
	return acronym
}

export function saveBlob(blob: Blob, filename: string) {
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.download = filename

	// Firefox requires the link in the document
	document.body.appendChild(link)
	link.click()
	link.remove()

	// Revoke after a tick to ensure the click finished
	setTimeout(() => URL.revokeObjectURL(url), 0)
}
